import { ReactNode } from 'react';

import MUIMenuItem from '@mui/material/MenuItem';

import { useStyles } from './MenuItem.styles';

/**
 * Свойства компонента {@link MenuItem}
 */
export interface MenuItemProps {
  /**
   * Дочерние узлы, которые будут отображены внутри элемента меню.
   */
  children: ReactNode | ReactNode[];

  /**
   * Флаг, определяющий, должен ли элемент меню быть отключён.
   *
   * @defaultValue `false`
   */
  disabled?: boolean;

  /**
   * Функция обратного вызова, которая будет вызвана при клике на элемент меню.
   */
  onClick?(): void;

  /**
   * Тестовый ID для элемента меню, который облегчает тестирование.
   */
  testId?: string;
}

/**
 * Компонент, который рендерит элемент меню с необязательным стилизированием, состоянием отключения и обработчиком клика.
 * Использует компонент `MenuItem` из Material-UI и может содержать любые ReactNode в качестве своих дочерних элементов.
 *
 * @public
 * @param children - Дочерние узлы, которые будут отображены внутри элемента меню.
 * @param onClick - Функция обратного вызова, которая будет вызвана при клике на элемент меню.
 * @param disabled - Флаг, определяющий, должен ли элемент меню быть отключён.
 * @param testId - Тестовый ID для элемента меню, который облегчает тестирование.
 * @returns  JSX-элемент, представляющий стилизованный элемент меню.
 *
 * @remarks
 *
 * Ссылка на свойства компонента {@link MenuItemProps}
 */
export const MenuItem = ({
  children,
  onClick,
  disabled,
  testId,
}: MenuItemProps) => {
  const { classes } = useStyles();

  return (
    <MUIMenuItem
      disabled={disabled}
      data-testid={testId}
      onClick={!disabled ? onClick : undefined}
      className={classes.menuItem}
    >
      {children}
    </MUIMenuItem>
  );
};
